import { DateTime } from 'luxon';
import { z } from 'zod';
import type { Occurrence } from './occurrences.js';
import { addDays, type LocalDate } from './time.js';

/** An event imported from a connected calendar (one occurrence; recurrences are expanded). */
export const externalEventSchema = z.object({
  id: z.uuid(),
  sourceId: z.uuid(),
  title: z.string(),
  location: z.string().nullable(),
  /** Timed events: absolute instants (ISO). All-day events: local dates, end exclusive. */
  allDay: z.boolean(),
  start: z.string(),
  end: z.string(),
  calendarName: z.string(),
  /** CSS colour of the calendar, e.g. "#0b8043". */
  color: z.string(),
  requiresCheck: z.boolean(),
});
export type ExternalEvent = z.infer<typeof externalEventSchema>;

export interface AllDayEvent {
  key: string;
  eventId: string;
  date: LocalDate;
  title: string;
  calendar: { name: string; color: string };
}

/**
 * Turns timed calendar events into per-day occurrences (an event crossing midnight appears on
 * every day it touches, clipped to that day) and lists all-day events per date.
 */
export function eventOccurrences(input: {
  events: readonly ExternalEvent[];
  from: LocalDate;
  to: LocalDate;
  zone: string;
}): { timed: Occurrence[]; allDay: AllDayEvent[] } {
  const timed: Occurrence[] = [];
  const allDay: AllDayEvent[] = [];

  for (const e of input.events) {
    const calendar = { name: e.calendarName, color: e.color };
    if (e.allDay) {
      const last = addDays(e.end, -1);
      for (
        let d = e.start < input.from ? input.from : e.start;
        d <= last && d <= input.to;
        d = addDays(d, 1)
      ) {
        allDay.push({ key: `${e.id}:${d}`, eventId: e.id, date: d, title: e.title, calendar });
      }
      continue;
    }
    const start = DateTime.fromISO(e.start, { zone: input.zone });
    const end = DateTime.fromISO(e.end, { zone: input.zone });
    if (!start.isValid || !end.isValid) continue;
    let day: LocalDate = start.toISODate();
    if (day < input.from) day = input.from;
    for (; day <= input.to; day = addDays(day, 1)) {
      const dayStart = DateTime.fromISO(day, { zone: input.zone });
      const dayEnd = dayStart.plus({ days: 1 });
      if (dayStart >= end && !(start.equals(end) && start >= dayStart && start < dayEnd)) break;
      const s = start > dayStart ? start : dayStart;
      const en = end < dayEnd ? end : dayEnd;
      if (en < s || (en.equals(s) && !start.equals(end))) continue;
      timed.push({
        key: `${e.id}:${day}`,
        source: 'event',
        itemId: e.id,
        date: day,
        originDate: day,
        title: e.title,
        kind: 'activity',
        priority: 'flex',
        color: null,
        requiresCheck: e.requiresCheck,
        notes: null,
        startTime: s.toFormat('HH:mm'),
        durationMin: Math.round(en.diff(s, 'minutes').minutes),
        start: s.toUTC().toISO() ?? e.start,
        end: en.toUTC().toISO() ?? e.end,
        moved: false,
        recurring: false,
        travelBeforeMin: 0,
        travelAfterMin: 0,
        calendar,
        location: e.location,
      });
    }
  }
  return { timed, allDay };
}

/** Merges own occurrences and calendar events into one chronological list. */
export function mergeOccurrences(...lists: Occurrence[][]): Occurrence[] {
  return lists
    .flat()
    .sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title, 'sl'));
}
